/**
 * Post-auth destination handling for the Login/Activation flows — the
 * allowlist + open-redirect guard the task requires.
 *
 * One thing lives here:
 *   - `safeNextRoute`: validate a caller-supplied `next` into a SAME-ORIGIN,
 *     in-app route only (a `/`-rooted path this SPA can navigate to), rejecting
 *     absolute URLs, protocol-relative `//evil.com`, and anything that would
 *     leave the app. An attacker who plants `?next=https://evil.com` (or a
 *     `javascript:` URI) in an invitation/login link must never be able to turn
 *     a successful sign-in into a redirect off-origin.
 */

import { isAtoAppProxySessionUrl } from "../api/launchClient";

/** Where an activation lands after success when no explicit `next` is given —
 *  Home (which IS the My Apps launcher under Personal Apps). Kept as an in-app
 *  route so it flows through the same `safeNextRoute` allowlist as any
 *  caller-supplied value. */
export const DEFAULT_POST_ACTIVATION_ROUTE = "/";

// Anything at or below the ASCII space codepoint (control chars, tab, newline,
// space) is never part of a real in-app route and is a classic request/redirect
// smuggling vector — reject it wholesale.
const CONTROL_OR_SPACE = /[\u0000-\u0020]/;
// A same-origin app route: exactly one leading slash, then a char that is
// neither a slash nor a backslash (rejects `//host`, `/\host`).
const SAME_ORIGIN_PATH = /^\/[^/\\]/;

/**
 * Normalize an untrusted `next` value to a safe in-app route (a string like
 * `/run` or `/a/foo`), or `null` when it isn't one. Only same-origin absolute
 * paths are accepted — never `//host`, `https://...`, `http:...`,
 * `javascript:...`, a bare `#...` hash, or a backslash-obfuscated variant.
 */
export function safeNextRoute(raw: string | null | undefined): string | null {
  if (!raw) return null;
  let value = raw.trim();
  if (value === "") return null;
  if (CONTROL_OR_SPACE.test(value)) return null;
  // Strip any fragment the caller tacked on — routing owns the hash itself.
  const hashIndex = value.indexOf("#");
  if (hashIndex >= 0) value = value.slice(0, hashIndex);
  if (value === "/") return "/";
  if (!SAME_ORIGIN_PATH.test(value)) return null;
  return value;
}

/**
 * Resolve the post-auth destination route: a validated `next`, else the given
 * fallback (itself validated), else `/`. Never returns an off-origin target.
 */
export function resolvePostAuthRoute(
  rawNext: string | null | undefined,
  fallback: string = "/",
): string {
  return safeNextRoute(rawNext) ?? safeNextRoute(fallback) ?? "/";
}

/**
 * Read the `next` route from the current location's hash params (the SPA's own
 * `#route=...&next=...` convention). Returns a validated route or null.
 */
export function readNextFromHash(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;
  return safeNextRoute(new URLSearchParams(hash).get("next"));
}

/**
 * Read the `next` route from the current location's QUERY string — the shape
 * `buildAtoSignInUrl` mints (`/sign-in?next=/settings`). Checked before the
 * hash form so a first-party link wins over a legacy fragment. Returns a
 * validated route or null.
 */
export function readNextFromQuery(): string | null {
  if (typeof window === "undefined") return null;
  return safeNextRoute(
    new URLSearchParams(window.location.search).get("next"),
  );
}

/**
 * The ONE absolute `next` a sign-in may follow: an Ato App host's own auth
 * recovery callback (`https://<label>.app.ato.run/__ato/auth/recovered#…`).
 * The path is fixed and served by Ato, not the App, so this is not an open
 * redirect into App content; the fragment only says popup vs. same tab.
 */
export const AUTH_RECOVERY_CALLBACK_PATH = "/__ato/auth/recovered";

export function safeAuthRecoveryReturnUrl(
  raw: string | null | undefined,
): string | null {
  if (!raw || CONTROL_OR_SPACE.test(raw) || raw.includes("\\")) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (
    !isAtoAppProxySessionUrl(url.toString()) ||
    url.username ||
    url.password ||
    url.pathname !== AUTH_RECOVERY_CALLBACK_PATH ||
    url.search
  ) {
    return null;
  }
  return url.toString();
}

export function readAuthRecoveryReturn(): string | null {
  if (typeof window === "undefined") return null;
  return safeAuthRecoveryReturnUrl(
    new URLSearchParams(window.location.search).get("next"),
  );
}

/** The `next` a sign-in screen should honour: query form first, then the
 *  legacy hash form. */
export function readSignInNext(): string | null {
  return readNextFromQuery() ?? readNextFromHash();
}
